import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiX, FiAlertTriangle } = FiIcons;

function DebugPanel() {
  const envVars = [
    {
      name: 'VITE_NCB_BASE_URL',
      description: 'Base URL for NoCodeBackend API',
      required: true,
      value: import.meta.env.VITE_NCB_BASE_URL,
      example: 'https://openapi.nocodebackend.com'
    },
    {
      name: 'VITE_NCB_INSTANCE_ID',
      description: 'NoCodeBackend Instance ID',
      required: true,
      value: import.meta.env.VITE_NCB_INSTANCE_ID,
      example: '53830_users'
    },
    {
      name: 'VITE_NCB_API_KEY',
      description: 'NoCodeBackend API key (Bearer token)',
      required: true,
      value: import.meta.env.VITE_NCB_API_KEY,
      example: 'your-ncb-api-key-here'
    },
    {
      name: 'VITE_DROPBOX_APP_KEY',
      description: 'Dropbox App Key (Client ID)',
      required: false,
      value: import.meta.env.VITE_DROPBOX_APP_KEY,
      example: 'your-dropbox-app-key'
    },
    {
      name: 'VITE_OPENAI_API_KEY',
      description: 'OpenAI API key (Bearer token)',
      required: false,
      value: import.meta.env.VITE_OPENAI_API_KEY,
      example: 'sk-your-openai-api-key-here'
    }
  ];

  const serverOnlyVars = [
    {
      name: 'DROPBOX_APP_SECRET',
      description: 'Dropbox App Secret (Client Secret)',
      note: 'Should be configured in Cloudflare Pages Functions environment'
    },
    {
      name: 'DROPBOX_REFRESH_TOKEN',
      description: 'Dropbox Refresh Token for token refresh',
      note: 'Should be configured in Cloudflare Pages Functions environment'
    },
    {
      name: 'OPENAI_API_KEY',
      description: 'OpenAI API key for server-side requests',
      note: 'Should be configured in Cloudflare Pages Functions environment'
    }
  ];

  const getStatusIcon = (value) => {
    return value ? FiCheck : FiX;
  };

  const getStatusText = (value, required) => {
    if (value) return 'Configured';
    return required ? 'Missing (Required)' : 'Not Set (Optional)';
  };

  const getStatusColor = (value, required) => {
    if (value) return 'text-green-600';
    return required ? 'text-red-600' : 'text-yellow-600';
  };

  const systemInfo = {
    environment: import.meta.env.MODE,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    online: navigator.onLine ? 'Online' : 'Offline',
    buildTime: new Date().toISOString(),
    userAgent: navigator.userAgent
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Debug Panel</h2>
        <p className="text-sm text-gray-500 mt-1">Environment configuration and system status</p>
      </div>

      <div>
        <h3 className="text-md font-medium text-gray-900 mb-4">Frontend Environment Variables</h3>
        <div className="space-y-4">
          {envVars.map((envVar) => (
            <div key={envVar.name} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <code className="text-sm font-mono bg-white px-2 py-1 rounded">
                      {envVar.name}
                    </code>
                    {envVar.required && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                        Required
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{envVar.description}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Example: <code>{envVar.example}</code>
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon 
                    icon={getStatusIcon(envVar.value)} 
                    className={getStatusColor(envVar.value, envVar.required)} 
                  />
                  <span className={`text-sm font-medium ${getStatusColor(envVar.value, envVar.required)}`}>
                    {getStatusText(envVar.value, envVar.required)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-md font-medium text-gray-900 mb-4">Server-Only Variables</h3>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-2">
            <SafeIcon icon={FiAlertTriangle} className="text-yellow-600 mt-0.5" />
            <p className="text-sm text-yellow-800">
              These variables must be set in Cloudflare Pages Functions environment, not in frontend code.
            </p>
          </div>
        </div>
        <div className="space-y-3">
          {serverOnlyVars.map((envVar) => (
            <div key={envVar.name} className="bg-gray-50 rounded-lg p-4">
              <code className="text-sm font-mono bg-white px-2 py-1 rounded">
                {envVar.name}
              </code>
              <p className="text-sm text-gray-600 mt-1">{envVar.description}</p>
              <p className="text-xs text-gray-500 mt-1">{envVar.note}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-md font-medium text-gray-900 mb-4">Configuration Instructions</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
            <li>Go to Cloudflare Pages project dashboard</li>
            <li>Navigate to Settings → Environment Variables</li>
            <li>Add missing environment variables</li>
            <li>Redeploy the application</li>
          </ol>
        </div>
      </div>

      <div>
        <h3 className="text-md font-medium text-gray-900 mb-4">System Information</h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm font-medium text-gray-700">Environment:</span>
              <span className="text-sm text-gray-600 ml-2">{systemInfo.environment}</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Timezone:</span>
              <span className="text-sm text-gray-600 ml-2">{systemInfo.timezone}</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Language:</span>
              <span className="text-sm text-gray-600 ml-2">{systemInfo.language}</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Status:</span>
              <span className={`text-sm ml-2 ${systemInfo.online === 'Online' ? 'text-green-600' : 'text-red-600'}`}>
                {systemInfo.online}
              </span>
            </div>
            <div className="md:col-span-2">
              <span className="text-sm font-medium text-gray-700">Build Time:</span>
              <span className="text-sm text-gray-600 ml-2">{systemInfo.buildTime}</span>
            </div>
            <div className="md:col-span-2">
              <span className="text-sm font-medium text-gray-700">User Agent:</span>
              <span className="text-sm text-gray-600 ml-2 break-all" title={systemInfo.userAgent}>
                {systemInfo.userAgent.substring(0, 100)}...
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DebugPanel;